/**
 * Stage 3.5 — neighborhood role consensus (pure).
 *
 * A physical surface should play ONE role within a local area: the slab top
 * is a floor, the slab underside a ceiling. Columns that mispair — e.g. the
 * slab underside is missing in a scan shadow, so the interval runs from the
 * storey below's floor up to THIS slab's top, labelling it a ceiling — put
 * the minority role on a surface most neighbours agree is the opposite.
 *
 * This pass makes the neighbourhood vote: for every interval endpoint, count
 * the neighbouring columns' floor and ceiling endpoints at the same level.
 * When the OPPOSING role outnumbers the endpoint's own role decisively, the
 * interval is dropped as a mispair. The vote is local (a few cells) because
 * levels legitimately change across a building (split levels, mezzanines) —
 * and the level tolerance stays below the thinnest plausible slab so a slab's
 * top and underside never vote against each other.
 */

import type { ColumnInterval } from './types';

export interface RoleConsensusOptions {
  /** Endpoints within this z-distance count as "the same level". Keep below
   *  the thinnest plausible slab. */
  levelToleranceM?: number;
  /** Chebyshev neighbourhood radius, in cells. */
  radiusCells?: number;
  /** The opposing role needs at least this many votes to overrule. */
  minOpposingVotes?: number;
  /** ...and at least this multiple of the supporting votes. */
  opposingRatio?: number;
}

const DEFAULTS: Required<RoleConsensusOptions> = {
  levelToleranceM: 0.1,
  radiusCells: 3,
  minOpposingVotes: 5,
  opposingRatio: 2,
};

interface ColumnEndpoints {
  floors: number[];
  ceilings: number[];
}

/** True when the opposing votes decisively outnumber the supporting votes. */
function overruled(supporting: number, opposing: number, opts: Required<RoleConsensusOptions>): boolean {
  return opposing >= opts.minOpposingVotes && opposing >= opts.opposingRatio * Math.max(1, supporting);
}

/**
 * Drop intervals whose floor endpoint sits on a level the neighbourhood
 * considers a ceiling (or vice versa). Returns a filtered copy plus the
 * dropped intervals per column (for debug role marking).
 */
export function consensusFilterIntervals(intervalsByColumn: ReadonlyArray<ColumnInterval[] | null>, nx: number, ny: number, options?: RoleConsensusOptions): { filtered: Array<ColumnInterval[] | null>; droppedByColumn: Map<number, ColumnInterval[]> } {
  const opts = { ...DEFAULTS, ...options };

  // Precompute each column's endpoint samples once.
  const endpoints: Array<ColumnEndpoints | null> = new Array<ColumnEndpoints | null>(intervalsByColumn.length).fill(null);
  for (let column = 0; column < intervalsByColumn.length; column++) {
    const intervals = intervalsByColumn[column];
    if (!intervals || intervals.length === 0) continue;
    endpoints[column] = {
      floors: intervals.map((iv) => iv.floorZ),
      ceilings: intervals.map((iv) => iv.ceilingZ),
    };
  }

  const votesAtLevel = (column: number, z: number): { floors: number; ceilings: number } => {
    const cx = column % nx;
    const cy = Math.floor(column / nx);
    let floors = 0;
    let ceilings = 0;
    for (let dy = -opts.radiusCells; dy <= opts.radiusCells; dy++) {
      const y = cy + dy;
      if (y < 0 || y >= ny) continue;
      for (let dx = -opts.radiusCells; dx <= opts.radiusCells; dx++) {
        const x = cx + dx;
        if (x < 0 || x >= nx) continue;
        const neighbour = y * nx + x;
        if (neighbour === column) continue;
        const ep = endpoints[neighbour];
        if (!ep) continue;
        for (const f of ep.floors) if (Math.abs(f - z) <= opts.levelToleranceM) floors++;
        for (const c of ep.ceilings) if (Math.abs(c - z) <= opts.levelToleranceM) ceilings++;
      }
    }
    return { floors, ceilings };
  };

  const filtered: Array<ColumnInterval[] | null> = new Array<ColumnInterval[] | null>(intervalsByColumn.length).fill(null);
  const droppedByColumn = new Map<number, ColumnInterval[]>();

  for (let column = 0; column < intervalsByColumn.length; column++) {
    const intervals = intervalsByColumn[column];
    if (!intervals || intervals.length === 0) continue;
    const kept: ColumnInterval[] = [];
    const dropped: ColumnInterval[] = [];
    for (const iv of intervals) {
      const floorVotes = votesAtLevel(column, iv.floorZ);
      const ceilingVotes = votesAtLevel(column, iv.ceilingZ);
      const floorMispaired = overruled(floorVotes.floors, floorVotes.ceilings, opts);
      const ceilingMispaired = overruled(ceilingVotes.ceilings, ceilingVotes.floors, opts);
      if (floorMispaired || ceilingMispaired) dropped.push(iv);
      else kept.push(iv);
    }
    if (kept.length > 0) filtered[column] = kept;
    if (dropped.length > 0) droppedByColumn.set(column, dropped);
  }

  return { filtered, droppedByColumn };
}
